/**
 * RoleBottomNav — mobile bottom navigation driven by Navigation Profile.
 *
 * Renders mobile_bottom_tabs from profile (up to 4 tabs).
 * When quick_action_items exist, a centered FAB is shown between tab 2 and 3.
 * Layout: Tab1 | Tab2 | FAB | Tab3 | Tab4 (5 slots with FAB)
 * Layout: Tab1 | Tab2 | Tab3 | Tab4 (4 slots without FAB)
 *
 * See: docs/architecture/TOP_LEVEL_NAVIGATION_ARCHITECTURE.md §4
 */

import { useTranslation } from 'react-i18next';

const TAB_ICONS = {
  today: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
    </svg>
  ),
  map: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
    </svg>
  ),
  farm: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  ),
  chat: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
    </svg>
  ),
  my_tasks: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  reports: (active) => (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
};

const TAB_FALLBACK = {
  today: 'Heute', map: 'Karte', farm: 'Betrieb', chat: 'Chat',
  my_tasks: 'Aufgaben', reports: 'Berichte',
};

function defaultIcon(active) {
  return (
    <svg className="w-5 h-5" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="4" strokeWidth={active ? 0 : 1.5} />
    </svg>
  );
}

function TabButton({ tabId, active, onClick, t }) {
  const iconFn = TAB_ICONS[tabId] || defaultIcon;
  const label = t(`nav.${tabId}`, { defaultValue: TAB_FALLBACK[tabId] || tabId });

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full min-w-0 transition-colors"
      style={{ color: active ? 'var(--aif-color-primary)' : 'var(--aif-text-muted)' }}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      data-testid={`bottom-tab-${tabId}`}
      data-tab={tabId}
    >
      {iconFn(active)}
      <span className="text-[10px] font-medium leading-none truncate">{label}</span>
    </button>
  );
}

export default function RoleBottomNav({ tabs = [], activeTab, onTabChange, showFab = false, onFabClick }) {
  const { t } = useTranslation();

  const leftTabs = tabs.slice(0, 2);
  const rightTabs = tabs.slice(2, 4);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[var(--aif-z-nav)] flex items-center justify-around
                 bg-[var(--aif-bg-surface)] border-t border-[var(--aif-border-default)]"
      style={{ height: 56, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      aria-label={t('nav.mainNavigation')}
      data-testid="bottom-nav"
    >
      {leftTabs.map((tabId) => (
        <TabButton key={tabId} tabId={tabId} active={activeTab === tabId} onClick={() => onTabChange(tabId)} t={t} />
      ))}

      {showFab && (
        <button
          onClick={onFabClick}
          className="flex items-center justify-center w-12 h-12 -mt-4 rounded-full
                     bg-[var(--aif-color-primary)] text-white shadow-lg"
          aria-label={t('nav.quickAction', { defaultValue: 'Schnellaktion' })}
          data-testid="bottom-fab"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      )}

      {rightTabs.map((tabId) => (
        <TabButton key={tabId} tabId={tabId} active={activeTab === tabId} onClick={() => onTabChange(tabId)} t={t} />
      ))}
    </nav>
  );
}
